import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import { CUSTOM_ICONS } from "@/components/ui-custom/CustomIcons";
import { Logo } from "@/components/ui-custom/Logo";
import { SITE, RESIDENTIAL_NAV, COMMERCIAL_NAV } from "@/data/site";

const NavDropdown = ({ label, items, testId, active }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const baseColor = active ? "var(--cta)" : "var(--text-light)";
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testId}
        aria-current={active ? "page" : undefined}
        className="flex items-center gap-1.5 py-2"
        style={{ color: baseColor, fontSize: "15px", fontWeight: active ? 600 : 500, borderBottom: `2px solid ${active ? "var(--cta)" : "transparent"}` }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = baseColor)}
      >
        {label}
        <ChevronDown size={15} strokeWidth={2} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className="absolute left-0 top-full pt-3"
        style={{ minWidth: "260px", visibility: open ? "visible" : "hidden", opacity: open ? 1 : 0, transform: open ? "translateY(0)" : "translateY(8px)", transition: "opacity 0.2s ease, transform 0.2s ease" }}
      >
        <div style={{ background: "var(--charcoal)", border: "1px solid var(--border-dark)", borderRadius: "8px", overflow: "hidden", boxShadow: "0 20px 40px rgba(0,0,0,0.4)" }}>
          {items.map((it) => {
            const Ico = CUSTOM_ICONS[it.icon] || Icons[it.icon] || Icons.ChevronRight;
            const isActive = pathname === it.path;
            return (
            <Link
              key={it.path}
              to={it.path}
              aria-current={isActive ? "page" : undefined}
              data-testid={`nav-drop-${it.path.split("/").pop()}`}
              className="flex items-center gap-3 px-5 py-3"
              style={{ color: isActive ? "var(--cta)" : "var(--text-light)", fontSize: "15px", fontWeight: isActive ? 600 : 400, background: isActive ? "rgba(201,162,75,0.12)" : "transparent", borderLeft: `2px solid ${isActive ? "var(--cta)" : "transparent"}`, transition: "background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(201,162,75,0.08)";
                e.currentTarget.style.borderLeftColor = "var(--cta)";
                e.currentTarget.style.color = "var(--cta)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isActive ? "rgba(201,162,75,0.12)" : "transparent";
                e.currentTarget.style.borderLeftColor = isActive ? "var(--cta)" : "transparent";
                e.currentTarget.style.color = isActive ? "var(--cta)" : "var(--text-light)";
              }}
            >
              <Ico size={17} strokeWidth={1.5} style={{ color: "var(--cta)" }} />
              {it.label}
            </Link>
          );})}
        </div>
      </div>
    </div>
  );
};

const MobileGroup = ({ title, items, onNavigate }) => (
  <div className="border-b" style={{ borderColor: "var(--border-dark)" }}>
    <div className="eyebrow eyebrow-start py-3">{title}</div>
    <div className="flex flex-col pb-3">
      {items.map((it) => {
        const Ico = CUSTOM_ICONS[it.icon] || Icons[it.icon] || Icons.ChevronRight;
        return (
        <Link
          key={it.path}
          to={it.path}
          onClick={onNavigate}
          data-testid={`mobile-nav-${it.path.split("/").pop()}`}
          className="flex items-center gap-3 py-2"
          style={{ color: "var(--text-light)", fontSize: "17px", fontWeight: 500 }}
        >
          <Ico size={18} strokeWidth={1.5} style={{ color: "var(--cta)" }} />
          {it.label}
        </Link>
      );})}
    </div>
  </div>
);

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className="sticky top-0 z-[100]"
        style={{
          height: "72px",
          transition: "background-color 0.35s ease, backdrop-filter 0.35s ease, border-color 0.35s ease",
          background: scrolled ? "rgba(9,9,11,0.72)" : "transparent",
          backdropFilter: scrolled ? "blur(18px) saturate(140%)" : "blur(5px)",
          WebkitBackdropFilter: scrolled ? "blur(18px) saturate(140%)" : "blur(5px)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(255,255,255,0.04)",
        }}
        data-testid="site-header"
      >
        <div className="container-1200 h-full flex items-center justify-between">
          <Logo heightClass="h-10 sm:h-[54px]" data-testid="header-logo" />

          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/" data-testid="nav-home" aria-current={location.pathname === "/" ? "page" : undefined} className="py-2" style={{ color: location.pathname === "/" ? "var(--cta)" : "var(--text-light)", fontSize: "15px", fontWeight: location.pathname === "/" ? 600 : 500, borderBottom: `2px solid ${location.pathname === "/" ? "var(--cta)" : "transparent"}` }} onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")} onMouseLeave={(e) => (e.currentTarget.style.color = location.pathname === "/" ? "var(--cta)" : "var(--text-light)")}>
              Home
            </Link>
            <NavDropdown label="Residential" items={RESIDENTIAL_NAV} testId="nav-residential" active={location.pathname.startsWith("/residential")} />
            <NavDropdown label="Commercial" items={COMMERCIAL_NAV} testId="nav-commercial" active={location.pathname.startsWith("/commercial")} />
            <Link to="/contact" data-testid="nav-contact" aria-current={location.pathname === "/contact" ? "page" : undefined} className="py-2" style={{ color: location.pathname === "/contact" ? "var(--cta)" : "var(--text-light)", fontSize: "15px", fontWeight: location.pathname === "/contact" ? 600 : 500, borderBottom: `2px solid ${location.pathname === "/contact" ? "var(--cta)" : "transparent"}` }} onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")} onMouseLeave={(e) => (e.currentTarget.style.color = location.pathname === "/contact" ? "var(--cta)" : "var(--text-light)")}>
              Contact
            </Link>
          </nav>

          <div className="flex items-center gap-3 lg:gap-4">
            <a
              href={SITE.phoneHref}
              data-testid="header-phone"
              aria-label={`Call ${SITE.phone}`}
              className="hidden lg:flex items-center justify-center"
              style={{ width: 44, height: 44, borderRadius: 999, border: "1.5px solid var(--cta)", color: "var(--cta)", transition: "background-color 0.2s ease, color 0.2s ease, transform 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--cta)"; e.currentTarget.style.color = "#17130a"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--cta)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <Phone size={18} strokeWidth={1.75} />
            </a>
            <Link to="/contact#quote" className="btn btn-primary hidden sm:inline-flex" data-testid="header-cta" style={{ padding: "12px 22px", fontSize: "13px" }}>
              Free Estimate
            </Link>
            <button className="lg:hidden" data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)} aria-label="Open menu" style={{ color: "var(--cta)" }}>
              <Menu size={28} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full-screen menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[300] lg:hidden overflow-y-auto" style={{ background: "var(--black)" }} data-testid="mobile-menu">
          <div className="container-1200 flex items-center justify-between" style={{ height: "60px" }}>
            <Logo heightClass="h-9" />
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" style={{ color: "var(--gold)" }}>
              <X size={28} strokeWidth={1.75} />
            </button>
          </div>
          <div className="container-1200 pb-16">
            <MobileGroup title="Residential" items={RESIDENTIAL_NAV} onNavigate={() => setMobileOpen(false)} />
            <MobileGroup title="Commercial" items={COMMERCIAL_NAV} onNavigate={() => setMobileOpen(false)} />
            <div className="flex flex-col py-4 gap-3 border-b" style={{ borderColor: "var(--border-dark)" }}>
              <Link to="/" data-testid="mobile-nav-home" onClick={() => setMobileOpen(false)} style={{ color: "var(--text-light)", fontSize: "17px", fontWeight: 600 }}>
                Home
              </Link>
              <Link to="/contact" data-testid="mobile-nav-contact" onClick={() => setMobileOpen(false)} style={{ color: "var(--text-light)", fontSize: "17px", fontWeight: 600 }}>
                Contact
              </Link>
            </div>
            <Link to="/contact#quote" data-testid="mobile-menu-cta" onClick={() => setMobileOpen(false)} className="btn btn-primary btn-block mt-6">
              Free Estimate
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
